import { listLink } from "../../ui/list-link";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast, together } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { pageHead } from "../empty";
import { schedulingScreen } from "./scheduling";
import { routineSettingsScreen } from "./routine";
import { table } from "../../ui/record";
import { clockWords } from "../../../server/modules/homecare/rules/care-tasks";
import { openModal } from "../../ui/modal";
import { archiveModal, field, pills, refuse, select } from "../clients/forms";
import { chip, ib } from "../../ui/record";
import { guardForm } from "../../ui/unsaved";

// Home Care › Settings (mockup modSettings "homecare"; brief 1C-4): everything that configures
// Home Care, as tiles to the screens that exist, and the company-wide Home Care settings. The
// verbal-orders setting moved here from Settings › Home care client types (DECISIONS 10/23: RN
// only, or "RN, or LPN with an RN co-sign within N hours"), with the same behaviour. Admin only
// (the setting's route); everyone else sees the tiles. Parts of the day (brief 1E-10, mockup v60):
// From and To for each part, saved by Admin or anyone with Settings at Edit.

const TILES: [string, string, string, string][] = [
  ["/settings/clienttypes", "Client types", "Defaults per client type", "organization"],
  // Brief 1E-17 (mockup v76).
  [
    "/homecare/settings/routine",
    "Home and routine",
    "Sections, photos and the first-visit card",
    "home",
  ],
  [
    "/homecare/settings/scheduling",
    "Scheduling",
    "Publishing, confirmations, open visits, shift patterns, matching",
    "calendar",
  ],
  ["/settings/workflows", "Workflows and stages", "Episode and visit stages", "workflow"],
  ["/settings/portals", "Portals", "Family and case manager access", "people"],
  ["/settings/modules", "Home Care features", "Turn parts of Home Care on or off", "sliders"],
];

export async function homeCareSettingsScreen(host: HTMLElement, ctx: ScreenContext) {
  // Settings › Home Care › Scheduling (brief 1D-2) opens at /homecare/settings/scheduling.
  if (ctx.record === "scheduling") return schedulingScreen(host, ctx);
  // Home and routine (brief 1E-17) opens at /homecare/settings/routine.
  if (ctx.record === "routine") return routineSettingsScreen(host, ctx);
  const verbalSlot = el("div");
  const signSlot = el("div");
  const partsSlot = el("div");
  const kindsSlot = el("div");
  host.append(
    pageHead(ctx, "Everything that sets up Home Care."),
    el(
      "div",
      { class: "grid3" },
      ...TILES.map(([to, title, sub, ic]) => {
        const b = el(
          "button",
          { type: "button", class: "card tile", style: "padding:16px" },
          el(
            "span",
            { style: "display:flex;gap:8px;align-items:center" },
            icon(ic),
            el("b", {}, title),
          ),
          el("span", { class: "muted" }, sub),
        );
        b.addEventListener("click", () => ctx.go(to));
        return b;
      }),
    ),
    verbalSlot,
    signSlot,
    el(
      "div",
      { class: "note" },
      el(
        "p",
        {},
        "Shared across modules: the requirements library, business profiles, integrations and users. Those are in Company settings.",
      ),
    ),
    partsSlot,
    kindsSlot,
  );

  // Brief 1E-28: signing an assessment with medications not reconciled, or without allergies or
  // code status: Flag (signed, with a flag) or Block (refused).
  async function drawSigning() {
    const res = await api<{
      medicationsAtSigning: string;
      atSigningChoices: { value: string; label: string }[];
    }>("GET", "/api/settings/home-care");
    if (!res.ok) return signSlot.replaceChildren();
    const v = res.body;
    const pick = el(
      "select",
      { id: "hc-atsign" },
      ...v.atSigningChoices.map((c) =>
        el("option", { value: c.value, selected: c.value === v.medicationsAtSigning }, c.label),
      ),
    );
    pick.addEventListener("change", async () => {
      const r = await api<unknown>("PUT", "/api/settings/home-care", {
        medicationsAtSigning: pick.value,
      });
      if (!r.ok) return toast("That wasn't saved", (r.body as { error?: string }).error);
      toast("Saved");
    });
    signSlot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Signing an assessment" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, "Signing an assessment"),
            el(
              "span",
              { class: "small muted" },
              "With medications not reconciled, or no allergies or code status.",
            ),
          ),
        ),
        el(
          "div",
          { class: "field", style: "max-width:220px" },
          el("label", { for: "hc-atsign" }, "When signed"),
          pick,
        ),
      ),
    );
  }

  // Verbal orders (company-wide): RN only, or LPN with an RN co-sign within N hours.
  async function drawVerbal() {
    const res = await api<{
      verbalOrders: { lpn: boolean; cosignHours: number };
      words: string;
      orderDays: number;
    }>("GET", "/api/settings/home-care");
    if (!res.ok) return verbalSlot.replaceChildren();
    const v = res.body;
    const lpn = el("input", { type: "checkbox", id: "vo-lpn", checked: v.verbalOrders.lpn });
    const hours = el("input", {
      type: "number",
      id: "vo-hours",
      min: "1",
      max: "72",
      value: String(v.verbalOrders.cosignHours),
      style: "max-width:90px",
    });
    const save = async () => {
      const r = await api<typeof v>("PUT", "/api/settings/home-care", {
        lpn: lpn.checked,
        cosignHours: Number(hours.value),
      });
      if (!r.ok) return toast("That wasn't saved", (r.body as { error?: string }).error);
      toast("Saved", `Verbal orders: ${r.body.words}`);
      void drawVerbal();
    };
    lpn.addEventListener("change", save);
    hours.addEventListener("change", save);
    verbalSlot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Verbal orders" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, "Verbal orders"),
            el(
              "span",
              { class: "small muted" },
              `Now: ${v.words}. Every order then goes to the physician to sign within ${v.orderDays} days.`,
            ),
          ),
        ),
        el(
          "div",
          { class: "field" },
          el("label", { for: "vo-lpn" }, lpn, " LPNs may take verbal orders, with an RN co-sign"),
        ),
        el(
          "div",
          { class: "field" },
          el("label", { for: "vo-hours" }, "The RN co-signs within (hours)"),
          hours,
        ),
      ),
    );
  }
  // Parts of the day: a task set for a part of the day shows on visits that cover it.
  type Part = { key: string; label: string; from: string; to: string; hours: string };
  async function drawParts() {
    const res = await api<{ parts: Part[]; canEdit: boolean }>("GET", "/api/settings/parts-of-day");
    if (!res.ok) return partsSlot.replaceChildren();
    const { parts, canEdit } = res.body;
    const inputs = new Map<string, { from: HTMLInputElement; to: HTMLInputElement }>();
    const box = (p: Part, which: "from" | "to") =>
      el("input", {
        type: "text",
        value: clockWords(p[which]),
        "aria-label": `${p.label} ${which === "from" ? "from" : "to"}`,
        style: "max-width:110px",
        disabled: !canEdit,
      });
    for (const p of parts) inputs.set(p.key, { from: box(p, "from"), to: box(p, "to") });
    const save = el("button", { type: "button", class: "btn sm pri" }, "Save");
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api<{ parts: Part[] }>(
        "PUT",
        "/api/settings/parts-of-day",
        Object.fromEntries(
          [...inputs].map(([k, x]) => [k, { from: x.from.value, to: x.to.value }]),
        ),
      );
      save.disabled = false;
      if (!r.ok) return toast("That wasn't saved", (r.body as { error?: string }).error);
      toast("Saved", "Parts of the day");
      void drawParts();
    });
    const section = el(
      "section",
      { class: "card", "aria-label": "Parts of the day" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("clock"), " Parts of the day"),
        canEdit ? save : null,
      ),
      table(
        [
          { h: "Part", f: (p: Part) => el("b", {}, p.label) },
          { h: "From", f: (p) => inputs.get(p.key)?.from ?? "" },
          { h: "To", f: (p) => inputs.get(p.key)?.to ?? "" },
        ],
        parts,
        undefined,
        "Parts of the day",
      ),
      el(
        "p",
        { class: "small muted", style: "margin-top:6px" },
        "A task set for a part of the day shows on visits that cover it. In 24-hour care, it goes to the shift that covers it.",
      ),
    );
    // Leaving with times typed and not saved asks first (brief 1E-23).
    guardForm(section, { what: "Parts of the day" });
    partsSlot.replaceChildren(section);
  }
  // What a file is (brief 1E-23): the company's list a patient or client file is filed as, and
  // where a patient file of each kind shows. Archived kinds stay on the files filed as them.
  type Kinds = {
    kinds: {
      id: string;
      name: string;
      recordKinds: ("patient" | "client")[];
      records: string;
      showsIn: string;
      showsInLabel: string;
      archived: { why: string } | null;
    }[];
    showsIn: { value: string; label: string }[];
    records: { value: string; label: string }[];
  };
  async function drawKinds() {
    const res = await api<Kinds>("GET", "/api/file-kinds");
    if (!res.ok) return kindsSlot.replaceChildren();
    const k = res.body;
    const canEdit = ctx.me.menus.some((m) => m.id === "settings");
    const kindModal = (row: Kinds["kinds"][number] | null) => {
      const name = field("Name", row?.name ?? "", { maxlength: "60" });
      const forWhom = pills(
        "For",
        "checkbox",
        k.records.map((r) => [r.value, r.label] as const),
        row?.recordKinds ?? ["patient"],
      );
      const shows = select(
        "Shows in",
        k.showsIn.map((x) => [x.value, x.label] as const),
        row?.showsIn ?? "documents",
      );
      const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
      save.addEventListener("click", async () => {
        save.disabled = true;
        const r = await api<Kinds>(
          row ? "PUT" : "POST",
          `/api/file-kinds${row ? `/${row.id}` : ""}`,
          {
            name: name.input.value,
            recordKinds: forWhom.values(),
            showsIn: shows.input.value,
          },
        );
        save.disabled = false;
        if (!r.ok) return refuse(m.note, r.body.error);
        m.close();
        toast("Saved", name.input.value);
        void drawKinds();
      });
      const m = openModal({
        title: row ? "Change this kind" : "Add a kind of file",
        body: [
          el("div", { class: "form", style: "max-width:none" }, name.el, forWhom.el, shows.el),
        ],
        actions: [save],
      });
    };
    kindsSlot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Kinds of file" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("file"), " Kinds of file"),
            el(
              "span",
              { class: "small muted" },
              "Where each kind shows. ",
              listLink("file_kind", "Add or rename kinds in Settings › Lists"),
            ),
          ),
          canEdit ? ib("plus", "Add a kind of file", () => kindModal(null)) : null,
        ),
        table(
          [
            {
              h: "Name",
              f: (r: Kinds["kinds"][number]) =>
                el("span", {}, el("b", {}, r.name), r.archived ? chip("Archived", "plain") : ""),
            },
            { h: "For", f: (r) => r.records },
            { h: "Shows in", f: (r) => r.showsInLabel },
            {
              h: "",
              f: (r) =>
                canEdit && !r.archived
                  ? el(
                      "span",
                      { class: "rowbtns" },
                      ib("pencil", "Change this kind", () => kindModal(r)),
                      ib("archive", "Archive this kind", () =>
                        archiveModal(r.name, `/api/file-kinds/${r.id}/archive`, () => drawKinds()),
                      ),
                    )
                  : "",
            },
          ],
          k.kinds,
          undefined,
          "Kinds of file",
        ),
      ),
    );
  }
  // One call for the screen's first view (brief 1E-29).
  await together(() => Promise.all([drawVerbal(), drawSigning(), drawParts(), drawKinds()]));
}
